import { createRng, type Address, type Rng } from '@lg/shared';
import type { D365Client } from '../client.js';
import { D365RequestError } from '../errors.js';
import {
  CreateCustomerInput,
  WorkCalendarSpec,
  EnsureAssignmentInput,
  EnsureJobInput,
  EnsurePayCycleInput,
  EnsurePersonUserInput,
  EnsurePositionInput,
  EnsureReportingLineInput,
  EnsureWorkerInput,
  type EnsurePositionResult,
  type EnsureResult,
  CreateLegalEntityInput,
  CreateProjectContractInput,
  CreateProjectInput,
  CreateVendorInput,
  type ApprovalLimit,
  type CopyProjectInfo,
  type CreateUserInput,
  type Customer,
  type D365User,
  type MasterDataGroups,
  type Project,
  type ProjectContract,
  type DmfStatus,
  type RecurringMessageStatus,
  type ImportFromPackageRequest,
  type ImportWorkflowRequest,
  type LegalEntity,
  type OverrideSpec,
  type Position,
  type RoleAssignment,
  type SecurityRole,
  type StartCopyRequest,
  type Vendor,
  type Worker,
} from '../types.js';
import {
  TEMPLATE_COMPANIES,
  buildMockState,
  ensureSeedPeople,
  copySourceFor,
  ensureMasterData,
  ensureTemplateCompanies,
  isTemplateCompany,
  masterDataGroupsFor,
} from './seed.js';
import type { MockExecution, MockFailTarget, MockState, MockStateStore } from './state.js';

export interface MockD365Options {
  store: MockStateStore;
  seed?: number;
  asOf?: Date;
  /** MOCK_D365_FAIL: make this operation fail once. */
  failOnce?: MockFailTarget | null;
  /** MOCK_COPY_SECONDS: how long copy and package jobs take. */
  copySeconds?: number;
  /** Artificial 150–600 ms latency per call, deterministic per seed. */
  latency?: boolean;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  /** Applies an imported package to mock state (wired in Phase 1 with the DMF package builder). */
  packageApplier?: (zip: Uint8Array, companyId: string, state: MockState) => void | Promise<void>;
}

const NOT_RUN_MS = 2_000;
const MOCK_BLOB_HOST = 'https://mock-d365.blob.local/dmf';

const COPY_PARTIAL_ERRORS = [
  "Entity 'Vendor posting profiles': 3 record(s) failed. Summary account 211100 does not exist in company {company}.",
  "Entity 'Number sequence references': 1 record(s) failed. Number sequence code PROJ_ID is already in use.",
];
const PEOPLE_PARTIAL_ERRORS = [
  "Entity 'Position worker assignments': row 4 failed. Worker {worker} is not employed in legal entity {company} on the assignment start date.",
  "Entity 'Position hierarchies': row 2 failed. Parent position does not exist for hierarchy type Line.",
];

/** Personnel number used in the injected people-import failure. */
const failingWorker = (companyId: string): string => `${companyId.slice(1)}004`;

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * In-memory D365 used for local dev, tests and the static demo build
 * (spec 12.3). State persists through the injected store. No deletes exist.
 */
export class MockD365Client implements D365Client {
  private readonly store: MockStateStore;
  private failOnce: MockFailTarget | null;
  private readonly copyMs: number;
  private readonly latency: boolean;
  private readonly now: () => number;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly latencyRng: Rng;
  private readonly packageApplier: MockD365Options['packageApplier'];
  private state: MockState;

  constructor(options: MockD365Options) {
    this.store = options.store;
    this.failOnce = options.failOnce ?? null;
    this.copyMs = (options.copySeconds ?? 45) * 1000;
    this.latency = options.latency ?? true;
    this.now = options.now ?? (() => Date.now());
    this.sleep = options.sleep ?? ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
    this.packageApplier = options.packageApplier;
    const seed = options.seed ?? 46;
    this.latencyRng = createRng(seed ^ 0x5eed);
    this.state = this.store.load() ?? buildMockState(seed, options.asOf ?? new Date(this.now()));
    // States saved by earlier builds gain the template companies added since.
    ensureTemplateCompanies(this.state);
    ensureMasterData(this.state);
    ensureSeedPeople(this.state);
    this.store.save(this.state);
  }

  /** Read-only snapshot for tests and diagnostics. */
  snapshot(): MockState {
    return structuredClone(this.state);
  }

  // ── plumbing ───────────────────────────────────────────────────────────────

  private async call<T>(fn: (s: MockState) => T): Promise<T> {
    if (this.latency) await this.sleep(this.latencyRng.int(150, 600));
    return structuredClone(fn(this.state));
  }

  private async write<T>(fn: (s: MockState) => T): Promise<T> {
    const result = await this.call(fn);
    this.store.save(this.state);
    return result;
  }

  private shouldFail(target: MockFailTarget, s: MockState): boolean {
    if (this.failOnce !== target || s.failuresConsumed.includes(target)) return false;
    s.failuresConsumed.push(target);
    return true;
  }

  private nextId(s: MockState): number {
    s.counter += 1;
    return s.counter;
  }

  private requireCompany(s: MockState, companyId: string): LegalEntity {
    const company = s.companies.find((c) => c.id === companyId.toUpperCase());
    if (!company) throw new D365RequestError(`Company ${companyId} does not exist.`, 404);
    return company;
  }

  private startExecution(
    s: MockState,
    id: string,
    kind: MockExecution['kind'],
    companyId: string,
    projectName: string,
    failed: boolean,
  ): void {
    const templates = kind === 'copy' ? COPY_PARTIAL_ERRORS : PEOPLE_PARTIAL_ERRORS;
    s.executions[id] = {
      kind,
      companyId,
      projectName,
      startedAtMs: this.now(),
      outcome: failed ? 'PartiallySucceeded' : 'Succeeded',
      errors: failed
        ? templates.map((t) =>
            t.replaceAll('{company}', companyId).replaceAll('{worker}', failingWorker(companyId)),
          )
        : [],
      applied: false,
    };
  }

  // ── lookups ────────────────────────────────────────────────────────────────

  isLegalEntityIdAvailable(id: string): Promise<boolean> {
    return this.call((s) => !s.companies.some((c) => c.id === id.trim().toUpperCase()));
  }

  getLegalEntity(id: string): Promise<LegalEntity | null> {
    return this.call((s) => s.companies.find((c) => c.id === id.trim().toUpperCase()) ?? null);
  }

  listVenueCompanies(): Promise<LegalEntity[]> {
    return this.call((s) => s.companies.filter((c) => !isTemplateCompany(c.id)));
  }

  searchUsers(query: string): Promise<D365User[]> {
    const q = query.trim().toLowerCase();
    return this.call((s) =>
      s.users.filter((u) => !q || u.upn.includes(q) || u.name.toLowerCase().includes(q)).slice(0, 20),
    );
  }

  getUser(userId: string): Promise<D365User | null> {
    return this.call((s) => s.users.find((u) => u.userId === userId) ?? null);
  }

  listSecurityRoles(): Promise<SecurityRole[]> {
    return this.call((s) => s.roles);
  }

  listVendors(companyId: string, query?: string): Promise<Vendor[]> {
    const q = query?.trim().toLowerCase() ?? '';
    return this.call((s) =>
      s.vendors.filter(
        (v) =>
          v.companyId === companyId.toUpperCase() &&
          (!q || v.name.toLowerCase().includes(q) || v.vendorId.toLowerCase().includes(q)),
      ),
    );
  }

  // ── master data ────────────────────────────────────────────────────────────

  listCustomers(companyId: string): Promise<Customer[]> {
    return this.call((s) => s.customers.filter((c) => c.companyId === companyId.toUpperCase()));
  }

  listMasterDataGroups(companyId: string): Promise<MasterDataGroups> {
    return this.call((s) => masterDataGroupsFor(s, companyId.toUpperCase()));
  }

  // People records (the live path of Import people). In mock mode the people package fills the
  // same collections; these keep the mock consistent when they're called.

  ensureJob(input: EnsureJobInput): Promise<EnsureResult> {
    const p = EnsureJobInput.parse(input);
    return this.write((s) => {
      // Seeded jobs are implied by the positions and signing limits that use them.
      const known =
        s.jobs.includes(p.jobId) ||
        s.positions.some((x) => x.jobId === p.jobId) ||
        s.approvalLimits.some((l) => l.jobId === p.jobId);
      if (known) return { created: false };
      s.jobs.push(p.jobId);
      return { created: true };
    });
  }

  ensureWorker(input: EnsureWorkerInput): Promise<EnsureResult> {
    const p = EnsureWorkerInput.parse(input);
    return this.write((s) => {
      const companyId = this.requireCompany(s, p.companyId).id;
      if (s.workers.some((w) => w.personnelNumber === p.personnelNumber)) return { created: false };
      s.workers.push({
        personnelNumber: p.personnelNumber,
        firstName: p.firstName,
        lastName: p.lastName,
        // Not linked to a user until ensurePersonUser (the worker's email isn't a user link).
        upn: null,
        companyId,
        employmentStart: p.startDate,
      });
      return { created: true };
    });
  }

  ensurePosition(input: EnsurePositionInput): Promise<EnsurePositionResult> {
    const p = EnsurePositionInput.parse(input);
    return this.write((s) => {
      const companyId = this.requireCompany(s, p.companyId).id;
      const existing = s.positions.find((x) =>
        p.positionId
          ? x.positionId === p.positionId
          : x.jobId === p.jobId && (p.anyCompany || x.companyId === companyId),
      );
      if (existing) return { created: false, positionId: existing.positionId };
      // Like the Position number sequence: the next six-digit number.
      const next =
        Math.max(0, ...s.positions.map((x) => (/^\d+$/.test(x.positionId) ? Number(x.positionId) : 0))) + 1;
      const positionId = p.positionId ?? String(next).padStart(6, '0');
      s.positions.push({
        positionId,
        title: p.description,
        jobId: p.jobId,
        companyId,
        departmentId: p.departmentId ?? null,
        reportsToPositionId: null,
        holderPersonnelNumber: null,
        holderName: null,
        holderUpn: null,
      });
      return { created: true, positionId };
    });
  }

  ensureReportingLine(input: EnsureReportingLineInput): Promise<EnsureResult> {
    const p = EnsureReportingLineInput.parse(input);
    return this.write((s) => {
      const pos = s.positions.find((x) => x.positionId === p.positionId);
      if (!pos) throw new D365RequestError(`Position ${p.positionId} does not exist.`, 400);
      if (pos.reportsToPositionId === p.parentPositionId) return { created: false };
      if (pos.reportsToPositionId)
        return { created: false, note: `already reports to ${pos.reportsToPositionId}` };
      pos.reportsToPositionId = p.parentPositionId;
      return { created: true };
    });
  }

  ensureAssignment(input: EnsureAssignmentInput): Promise<EnsureResult> {
    const p = EnsureAssignmentInput.parse(input);
    return this.write((s) => {
      const pos = s.positions.find((x) => x.positionId === p.positionId);
      const worker = s.workers.find((w) => w.personnelNumber === p.personnelNumber);
      if (!pos || !worker)
        throw new D365RequestError(
          `Position ${p.positionId} or worker ${p.personnelNumber} does not exist.`,
          400,
        );
      if (pos.holderPersonnelNumber === p.personnelNumber) return { created: false };
      pos.holderPersonnelNumber = worker.personnelNumber;
      pos.holderName = `${worker.firstName} ${worker.lastName}`;
      pos.holderUpn = worker.upn;
      return { created: true };
    });
  }

  ensurePayCycle(input: EnsurePayCycleInput): Promise<EnsureResult> {
    const p = EnsurePayCycleInput.parse(input);
    return this.write((s) => {
      s.payCycles ??= ['M'];
      if (s.payCycles.includes(p.id)) return { created: false };
      s.payCycles.push(p.id);
      return { created: true };
    });
  }

  findWorkerForUser(userId: string): Promise<{ personnelNumber: string } | null> {
    return this.call((s) => {
      const user = s.users.find((u) => u.userId.toLowerCase() === userId.toLowerCase());
      const w = user && s.workers.find((x) => x.upn?.toLowerCase() === user.upn.toLowerCase());
      return w ? { personnelNumber: w.personnelNumber } : null;
    });
  }

  ensurePersonUser(input: EnsurePersonUserInput): Promise<EnsureResult> {
    const p = EnsurePersonUserInput.parse(input);
    return this.write((s) => {
      const user = s.users.find((u) => u.userId === p.userId);
      const worker = s.workers.find((w) => w.personnelNumber === p.personnelNumber);
      if (!user || !worker)
        throw new D365RequestError(`User ${p.userId} or worker ${p.personnelNumber} does not exist.`, 400);
      if (worker.upn === user.upn) return { created: false };
      worker.upn = user.upn;
      return { created: true };
    });
  }

  ensureWorkCalendar(input: WorkCalendarSpec): Promise<{ created: boolean }> {
    const p = WorkCalendarSpec.parse(input);
    return this.write((s) => {
      const companyId = this.requireCompany(s, p.companyId).id;
      if (s.workCalendars.some((c) => c.companyId === companyId && c.calendarId === p.calendarId))
        return { created: false };
      s.workCalendars.push({ companyId, calendarId: p.calendarId, name: p.name });
      return { created: true };
    });
  }

  createCustomer(input: CreateCustomerInput): Promise<Customer> {
    const p = CreateCustomerInput.parse(input);
    return this.write((s) => {
      const companyId = this.requireCompany(s, p.companyId).id;
      const account = p.customerAccount.toUpperCase();
      if (s.customers.some((c) => c.companyId === companyId && c.customerAccount === account))
        throw new D365RequestError(`Customer ${account} already exists in ${companyId}.`, 409);
      if (!masterDataGroupsFor(s, companyId).customerGroups.includes(p.group))
        throw new D365RequestError(`Customer group ${p.group} does not exist in company ${companyId}.`, 400);
      const customer: Customer = {
        customerAccount: account,
        name: p.name,
        group: p.group,
        currency: p.currency,
        companyId,
      };
      s.customers.push(customer);
      return customer;
    });
  }

  createVendor(input: CreateVendorInput): Promise<Vendor> {
    const p = CreateVendorInput.parse(input);
    return this.write((s) => {
      const companyId = this.requireCompany(s, p.companyId).id;
      const account = p.vendorAccount.toUpperCase();
      if (s.vendors.some((v) => v.companyId === companyId && v.vendorId === account))
        throw new D365RequestError(`Vendor ${account} already exists in ${companyId}.`, 409);
      if (!masterDataGroupsFor(s, companyId).vendorGroups.includes(p.group))
        throw new D365RequestError(`Vendor group ${p.group} does not exist in company ${companyId}.`, 400);
      const vendor: Vendor = { vendorId: account, name: p.name, companyId };
      s.vendors.push(vendor);
      return vendor;
    });
  }

  listProjectContracts(companyId: string): Promise<ProjectContract[]> {
    return this.call((s) => s.projectContracts.filter((c) => c.companyId === companyId.toUpperCase()));
  }

  createProjectContract(input: CreateProjectContractInput): Promise<ProjectContract> {
    const p = CreateProjectContractInput.parse(input);
    return this.write((s) => {
      const companyId = this.requireCompany(s, p.companyId).id;
      const contractId = p.contractId.toUpperCase();
      if (s.projectContracts.some((c) => c.companyId === companyId && c.contractId === contractId))
        throw new D365RequestError(`Project contract ${contractId} already exists in ${companyId}.`, 409);
      if (!s.customers.some((c) => c.companyId === companyId && c.customerAccount === p.customerAccount))
        throw new D365RequestError(
          `Funding source: customer ${p.customerAccount} does not exist in company ${companyId}.`,
          400,
        );
      const contract: ProjectContract = {
        contractId,
        name: p.name,
        customerAccount: p.customerAccount,
        companyId,
      };
      s.projectContracts.push(contract);
      return contract;
    });
  }

  listProjects(companyId: string): Promise<Project[]> {
    return this.call((s) => s.projects.filter((x) => x.companyId === companyId.toUpperCase()));
  }

  createProject(input: CreateProjectInput): Promise<Project> {
    const p = CreateProjectInput.parse(input);
    return this.write((s) => {
      const companyId = this.requireCompany(s, p.companyId).id;
      const projectId = p.projectId.toUpperCase();
      const fail = (why: string): never => {
        throw new D365RequestError(`Project ${projectId}: ${why}`, 400);
      };
      if (s.projects.some((x) => x.companyId === companyId && x.projectId === projectId))
        throw new D365RequestError(`Project ${projectId} already exists in ${companyId}.`, 409);
      if (!masterDataGroupsFor(s, companyId).projectGroups.includes(p.projectGroup))
        fail(`project group ${p.projectGroup} does not exist in company ${companyId}.`);
      if (
        p.contractId &&
        !s.projectContracts.some((c) => c.companyId === companyId && c.contractId === p.contractId)
      )
        fail(`project contract ${p.contractId} does not exist.`);
      if (
        p.parentProjectId &&
        !s.projects.some((x) => x.companyId === companyId && x.projectId === p.parentProjectId)
      )
        fail(`parent project ${p.parentProjectId} does not exist.`);
      for (const worker of [p.managerPersonnelNumber, p.controllerPersonnelNumber]) {
        if (worker && !s.workers.some((w) => w.companyId === companyId && w.personnelNumber === worker))
          fail(`worker ${worker} is not employed in ${companyId}.`);
      }
      const project: Project = {
        projectId,
        name: p.name,
        projectType: p.projectType,
        projectGroup: p.projectGroup,
        contractId: p.contractId ?? null,
        parentProjectId: p.parentProjectId ?? null,
        companyId,
      };
      s.projects.push(project);
      return project;
    });
  }

  // ── people (read) ──────────────────────────────────────────────────────────

  getPositions(companyId: string): Promise<Position[]> {
    return this.call((s) => s.positions.filter((p) => p.companyId === companyId.toUpperCase()));
  }

  getPosition(positionId: string): Promise<Position | null> {
    return this.call((s) => s.positions.find((p) => p.positionId === positionId.toUpperCase()) ?? null);
  }

  getWorkers(companyId: string): Promise<Worker[]> {
    return this.call((s) => s.workers.filter((w) => w.companyId === companyId.toUpperCase()));
  }

  getApprovalLimits(companyId: string): Promise<ApprovalLimit[]> {
    return this.call((s) => s.approvalLimits.filter((l) => l.companyId === companyId.toUpperCase()));
  }

  listUserRoleAssignments(companyId: string): Promise<RoleAssignment[]> {
    return this.call((s) => s.roleAssignments.filter((a) => a.companyId === companyId.toUpperCase()));
  }

  // ── writes ─────────────────────────────────────────────────────────────────

  createLegalEntity(input: CreateLegalEntityInput): Promise<LegalEntity> {
    const parsed = CreateLegalEntityInput.parse(input);
    return this.write((s) => {
      if (this.shouldFail('createLegalEntity', s)) {
        throw new D365RequestError(
          `Legal entity ${parsed.id} could not be created: the number sequence for party ID is exhausted.`,
          400,
        );
      }
      const id = parsed.id.toUpperCase();
      if (s.companies.some((c) => c.id === id)) {
        throw new D365RequestError(`Company ${id} already exists.`, 409);
      }
      const entity: LegalEntity = {
        id,
        name: parsed.name,
        searchName: parsed.searchName,
        timeZone: parsed.timeZone,
        address: parsed.address ? { ...parsed.address } : null,
        region: parsed.region,
      };
      s.companies.push(entity);
      s.entityCounts[id] = {};
      return entity;
    });
  }

  setLegalEntityAddress(companyId: string, address: Address): Promise<void> {
    return this.write((s) => {
      this.requireCompany(s, companyId).address = { ...address };
    });
  }

  setTaxRegistration(companyId: string, ein: string): Promise<void> {
    return this.write((s) => {
      this.requireCompany(s, companyId);
      s.taxRegistrationLast4[companyId.toUpperCase()] = ein.replace(/\D/g, '').slice(-4);
    });
  }

  applyOverride(companyId: string, override: OverrideSpec): Promise<void> {
    return this.write((s) => {
      const id = this.requireCompany(s, companyId).id;
      s.overrides[id] = { ...s.overrides[id], [override.key]: override.value };
    });
  }

  createUser(input: CreateUserInput): Promise<D365User> {
    return this.write((s) => {
      const existing = s.users.find((u) => u.userId === input.userId || u.upn === input.upn.toLowerCase());
      if (existing) throw new D365RequestError(`User ${input.userId} already exists.`, 409);
      const user: D365User = {
        userId: input.userId,
        upn: input.upn.toLowerCase(),
        name: input.name,
        enabled: true,
      };
      s.users.push(user);
      return user;
    });
  }

  assignUserRole(userId: string, roleId: string, companyId: string): Promise<void> {
    return this.write((s) => {
      if (this.shouldFail('security', s)) {
        throw new D365RequestError(
          `Role ${roleId} could not be assigned to ${userId}: the user is not enabled for company ${companyId}.`,
          400,
        );
      }
      const id = this.requireCompany(s, companyId).id;
      if (!s.users.some((u) => u.userId === userId))
        throw new D365RequestError(`User ${userId} not found.`, 404);
      if (!s.roles.some((r) => r.roleId === roleId))
        throw new D365RequestError(`Role ${roleId} not found.`, 404);
      const exists = s.roleAssignments.some(
        (a) => a.userId === userId && a.roleId === roleId && a.companyId === id,
      );
      if (!exists) s.roleAssignments.push({ userId, roleId, companyId: id });
    });
  }

  addCompanyToPolicy(policy: 'SigningLimit' | 'Purchasing', companyId: string): Promise<void> {
    return this.write((s) => {
      const id = this.requireCompany(s, companyId).id;
      if (!s.policies[policy].includes(id)) s.policies[policy].push(id);
    });
  }

  setApprovalLimits(companyId: string, limits: ApprovalLimit[]): Promise<void> {
    return this.write((s) => {
      const id = this.requireCompany(s, companyId).id;
      s.approvalLimits = [
        ...s.approvalLimits.filter((l) => l.companyId !== id),
        ...limits.map((l) => ({ ...l, companyId: id })),
      ];
    });
  }

  setRequireExplicitlyAssignedUsers(companyId: string, value: boolean): Promise<void> {
    return this.write((s) => {
      s.requireExplicitlyAssigned[this.requireCompany(s, companyId).id] = value;
    });
  }

  // ── data management package API ───────────────────────────────────────────

  getAzureWriteUrl(uniqueFileName: string): Promise<{ blobId: string; blobUrl: string }> {
    return this.write((s) => {
      const blobId = `blob-${this.nextId(s)}`;
      return {
        blobId,
        blobUrl: `${MOCK_BLOB_HOST}/${blobId}/${encodeURIComponent(uniqueFileName)}?sig=mock`,
      };
    });
  }

  uploadPackage(blobUrl: string, zip: Uint8Array): Promise<void> {
    return this.write((s) => {
      if (!blobUrl.startsWith(MOCK_BLOB_HOST)) throw new D365RequestError('Unknown blob URL.', 400);
      s.blobs[blobUrl] = toBase64(zip);
    });
  }

  importFromPackage(req: ImportFromPackageRequest): Promise<{ executionId: string }> {
    return this.write((s) => {
      if (!(req.packageUrl in s.blobs)) throw new D365RequestError('Package was not uploaded.', 400);
      const companyId = this.requireCompany(s, req.legalEntityId).id;
      const executionId = `${req.definitionGroupId}-${companyId}-${this.nextId(s)}`;
      this.startExecution(
        s,
        executionId,
        'people',
        companyId,
        req.definitionGroupId,
        this.shouldFail('people', s),
      );
      // Remember which package belongs to this execution so it can be applied on success.
      s.blobs[`exec:${executionId}`] = s.blobs[req.packageUrl] ?? '';
      return { executionId };
    });
  }

  async getExecutionStatus(executionId: string): Promise<DmfStatus> {
    const { status, applyPackage, companyId } = await this.write((s) => {
      const exec = s.executions[executionId];
      if (!exec) return { status: 'Unknown' as DmfStatus, applyPackage: null, companyId: '' };
      const elapsed = this.now() - exec.startedAtMs;
      const notRunMs = Math.min(NOT_RUN_MS, this.copyMs / 4);
      if (elapsed < notRunMs) return { status: 'NotRun' as DmfStatus, applyPackage: null, companyId: '' };
      if (elapsed < this.copyMs)
        return { status: 'Executing' as DmfStatus, applyPackage: null, companyId: '' };
      let applyPackage: string | null = null;
      if (!exec.applied) {
        exec.applied = true;
        if (exec.kind === 'copy') this.applyCopy(s, exec);
        else if (exec.outcome === 'Succeeded') applyPackage = s.blobs[`exec:${executionId}`] ?? null;
      }
      return { status: exec.outcome as DmfStatus, applyPackage, companyId: exec.companyId };
    });
    if (applyPackage && this.packageApplier) {
      // A processed package lands in mock D365, so read-back checks see real data.
      await this.packageApplier(fromBase64(applyPackage), companyId, this.state);
      this.store.save(this.state);
    }
    return status;
  }

  private applyCopy(s: MockState, exec: MockExecution): void {
    const template = s.entityCounts[copySourceFor(exec.projectName)] ?? {};
    const counts = { ...template };
    // A partial copy leaves the failing entities short.
    if (exec.outcome === 'PartiallySucceeded') {
      counts['VendorPostingProfiles'] = Math.max(0, (counts['VendorPostingProfiles'] ?? 0) - 3);
    }
    s.entityCounts[exec.companyId] = { ...s.entityCounts[exec.companyId], ...counts };
    // The copy brings the template's vendors and customers along.
    const source = copySourceFor(exec.projectName);
    for (const v of s.vendors.filter((x) => x.companyId === source)) {
      if (!s.vendors.some((x) => x.companyId === exec.companyId && x.vendorId === v.vendorId))
        s.vendors.push({ ...v, companyId: exec.companyId });
    }
    for (const c of s.customers.filter((x) => x.companyId === source)) {
      if (!s.customers.some((x) => x.companyId === exec.companyId && x.customerAccount === c.customerAccount))
        s.customers.push({ ...c, companyId: exec.companyId });
    }
  }

  // ── recurring integrations (data job enqueue) ─────────────────────────────

  enqueueRecurringPackage(req: {
    activityId: string;
    company: string;
    zip: Uint8Array;
  }): Promise<{ messageId: string }> {
    return this.write((s) => {
      const companyId = this.requireCompany(s, req.company).id;
      const messageId = `MSG-${companyId}-${String(this.nextId(s)).padStart(4, '0')}`;
      this.startExecution(s, messageId, 'people', companyId, req.activityId, this.shouldFail('people', s));
      s.blobs[`exec:${messageId}`] = toBase64(req.zip);
      return { messageId };
    });
  }

  async getRecurringMessageStatus(messageId: string): Promise<RecurringMessageStatus> {
    const status = await this.getExecutionStatus(messageId);
    const map: Record<DmfStatus, RecurringMessageStatus> = {
      Unknown: 'Unknown',
      NotRun: 'Enqueued',
      Executing: 'Processing',
      Succeeded: 'Processed',
      PartiallySucceeded: 'ProcessedWithErrors',
      Failed: 'PreProcessingError',
      Canceled: 'PreProcessingError',
    };
    return map[status];
  }

  /** Demo control: make the next call of this operation fail once (MOCK_D365_FAIL at runtime). */
  setFailOnce(target: MockFailTarget | null): void {
    this.failOnce = target;
    if (target) this.state.failuresConsumed = this.state.failuresConsumed.filter((t) => t !== target);
    this.store.save(this.state);
  }

  get pendingFailure(): MockFailTarget | null {
    return this.failOnce && !this.state.failuresConsumed.includes(this.failOnce) ? this.failOnce : null;
  }

  getExecutionErrors(executionId: string): Promise<string[]> {
    return this.call((s) => s.executions[executionId]?.errors ?? []);
  }

  getImportTargetErrorKeys(executionId: string, entityName: string): Promise<string[]> {
    return this.call((s) => {
      const exec = s.executions[executionId];
      if (!exec || exec.outcome === 'Succeeded') return [];
      return entityName === 'PositionWorkerAssignments' ? [failingWorker(exec.companyId)] : [];
    });
  }

  // ── custom X++ services ────────────────────────────────────────────────────

  getCopyProjectInfo(projectName: string): Promise<CopyProjectInfo> {
    return this.call((s) => {
      const exists =
        projectName === 'LG-VENUE-PEOPLE' || TEMPLATE_COMPANIES.some((t) => t.copyProject === projectName);
      const source = copySourceFor(projectName);
      const day = 24 * 60 * 60 * 1000;
      return {
        exists,
        sourceCompany: source,
        entityCount: exists ? Object.keys(s.entityCounts[source] ?? {}).length : 0,
        lastModified: new Date(Date.parse(s.asOf) - 14 * day).toISOString(),
      };
    });
  }

  startCopy(req: StartCopyRequest): Promise<{ executionIds: string[] }> {
    return this.write((s) => {
      const companyId = this.requireCompany(s, req.destinationCompany).id;
      const failed = this.shouldFail('copy', s);
      const run = this.nextId(s);
      const ids = [`${req.projectName}-${companyId}-${run}-1`];
      if (req.copyNumberSequences) ids.push(`${req.projectName}-${companyId}-${run}-2`);
      ids.forEach((id, i) =>
        this.startExecution(s, id, 'copy', companyId, req.projectName, failed && i === 0),
      );
      return { executionIds: ids };
    });
  }

  importWorkflow(req: ImportWorkflowRequest): Promise<{ workflowId: string; active: boolean }> {
    return this.write((s) => {
      if (this.shouldFail('workflows', s)) {
        throw new D365RequestError(
          `Workflow ${req.workflowKey} import failed: the workflow type is not enabled in company ${req.company}.`,
          400,
        );
      }
      const id = this.requireCompany(s, req.company).id;
      const workflow = { workflowId: `${id}-${req.workflowKey}-${this.nextId(s)}`, active: req.activate };
      s.workflows[id] = { ...s.workflows[id], [req.workflowKey]: workflow };
      return workflow;
    });
  }

  // ── validation ─────────────────────────────────────────────────────────────

  countRecords(entitySet: string, companyId: string): Promise<number> {
    return this.call((s) => s.entityCounts[companyId.toUpperCase()]?.[entitySet] ?? 0);
  }

  isWorkflowActive(companyId: string, workflowKey: string): Promise<boolean> {
    return this.call((s) => s.workflows[companyId.toUpperCase()]?.[workflowKey]?.active ?? false);
  }
}
